import React from "react";
import {useAuth} from "../../contexts/authContext";
import {useDarkMode} from "../../contexts/darkModeContext";
import {formatTimestamp, salesCodeMapping} from "../GLOBAL/GlobalFunctions";
import {calculateTimeDifference} from "../Performance";

import {
    ArrowTrendingUpIcon,
    BoltIcon,
    CheckBadgeIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon, XCircleIcon, FireIcon, MinusCircleIcon, RocketLaunchIcon
} from "@heroicons/react/24/outline";
import PerformanceScoreRing from "./PerformanceScoreRing";
import ERPBadgeList from "./ERPBadgeList";

const PerformanceCard = ({ order, CSR, index, avg, short = false }) => {
    
    const { darkMode } = useDarkMode();
    const { currentUser } = useAuth();
    
    const average = avg;
    
    function getTimeScore(submittedTime, csrTime) {
      const minutes = calculateTimeDifference(submittedTime, csrTime, true);

      // Map response time to a 0–12 scale for the score ring
      if (minutes <= 45) return 12;      // Green
      if (minutes <= 90) return 6;       // Orange
      return 2;                          // Red
    }
    
    const score = getTimeScore(order.submittedTime, order.csrTime);
    
    const scoreInterpretation = [
      {
        min: 10,
        label: "Fast response",
        short: "Fast",
        reason: "Response time under 45 minutes",
        color: "text-green-600",
        icon: RocketLaunchIcon
      },
      {
        min: 5,
        label: "Moderate response",
        short: "Moderate",
        reason: "Response time between 46–90 minutes",
        color: "text-orange-500",
        icon: ExclamationTriangleIcon
      },
      {
        min: 0,
        label: "Slow response",
        short: "Slow",
        reason: "Response time greater than 91 minutes",
        color: "text-red-600",
        icon: XCircleIcon
      }
    ];
    
    
    function getScoreMeta(score) {
      const clamped = Math.max(0, Math.min(score, 12));
    
      const result = scoreInterpretation.find(item => clamped >= item.min);
    
      return {
        score: clamped,
        label: result.label,
        reason: result.reason,
        color: result.color,
        icon: result.icon,
        short: result.short
      };
    }
    
    const meta = getScoreMeta(score);

    const Icon = meta.icon
    
    function isOutsideOfficeHours(timestamp) {
      if (!timestamp) return false;
    
      const date = new Date(timestamp);
      const day = date.getDay(); // 0 = Sun, 6 = Sat
      const hour = date.getHours();
    
      const isWeekend = day === 0 || day === 6;
      const beforeOpen = hour < 8;
      const afterClose = hour >= 17;
    
      return isWeekend || beforeOpen || afterClose;
    }
    
    function getSubmissionContext(timestamp) {
      const date = new Date(timestamp);
      const day = date.getDay();
      const hour = date.getHours();
    
      const weekend = day === 0 || day === 6;
      const beforeOpen = hour < 8;
      const afterClose = hour >= 17;
    
      return {
        outsideOfficeHours: weekend || beforeOpen || afterClose,
        weekend,
        beforeOpen,
        afterClose
      };
    }
    
    const submittedAfterHours = isOutsideOfficeHours(order.submittedTime);
    
    return (
        
        <div className={`col-span-full relative z-1 border-b border-opacity-50 ${darkMode ? 'border-neutral-700' : ''} ${submittedAfterHours ? 'bg-blue-500/10' : ''}`}>
            
            {/*Body*/}
            <div className={`grid ${short ? 'grid-cols-6' : 'grid-cols-6'} p-2 gap-x-2`}>
              
                {/*Badge*/}
                <div className="col-span-1 flex items-center gap-x-3">
                    
                    <CheckBadgeIcon className={`size-10 ${meta.color}`}/>
                    
                    <div className="grow">
                        <div className={`flex justify-between`}>
                            <span className={`text-gray-500 text-xs ${short ? 'hidden' : ''}`}>Order For:</span>
                            <span className={`text-green-600 text-xs italic ${order?.customerCode ? '' : 'hidden'}`}>{order?.customerCode || "------"}</span>
                        </div>
                        <h3 className={`font-medium ${short ? 'text-xxs' : 'text-sm -mt-1'}`}>{salesCodeMapping[order.salesCode] || "Sales Rep Not Found"}</h3>
                    </div>
                </div>
        
                {/*Times*/}
                <div className={`${short ? 'col-span-3' : 'col-span-2'} items-center grid grid-cols-2 gap-x-3`}>
                    
                    {/*Submitted*/}
                    <div className="col-span-1 flex flex-col gap-y-1">
                        <span className="text-[13px] text-muted-foreground-1 text-gray-500">
                          Submitted On:
                        </span>
                
                        <div className="flex items-center gap-x-1.5">
                            <ClockIcon className={`size-4 text-pendingColor`}/>
                            <span className="font-medium text-xs text-foreground">{formatTimestamp(order.submittedTime)}</span>
                        </div>
                    </div>
            
                    {/*Finalized*/}
                    <div className="col-span-1 flex flex-col gap-y-1">
                        <span className="text-[13px] text-muted-foreground-1 text-gray-500">
                          Finalized At:
                        </span>
                
                        <div className="flex items-center gap-x-1.5">
                            <CheckCircleIcon className={`size-4 text-green-600`}/>
                            <span className="font-medium text-xs text-foreground">{formatTimestamp(order.csrTime)}</span>
                        </div>
                    </div>
                    
                </div>
                
                {/*Duration*/}
                <li className="col-span-1 flex justify-between items-center gap-x-2">
                      <div className="flex flex-col gap-y-1">
                        <span className="block text-xs text-gray-500 text-muted-foreground-1">
                          Duration
                        </span>
                        <span className="block font-medium text-xs text-foreground">
                          {calculateTimeDifference(order.submittedTime, order.csrTime)}
                        </span>
                      </div>
            
                      {/*<div className="flex flex-col justify-end items-end gap-1">*/}
                      {/*    */}
                      {/*    <p className="text-xxs text-primary flex items-center gap-x-1">*/}
                      {/*        {meta.short === 'Average' ? (*/}
                      {/*          <CheckBadgeIcon className={`size-4 ${meta.color} ${meta.short === 'Below' ? 'rotate-90' : ''} duration-500`}/>*/}
                      {/*        ) : (*/}
                      {/*          <ArrowTrendingUpIcon className={`size-4 ${meta.color} ${meta.short === 'Below' ? 'rotate-90' : ''} duration-500`}/>*/}
                      {/*        )}*/}
                      {/*        /!*<ArrowTrendingUpIcon className={`size-4 ${meta.color} ${meta.short === 'Below' ? 'rotate-90' : ''} duration-500`}/>*!/*/}
                      {/*        <span className={`${meta.color}`}>{meta.short}</span>*/}
                      {/*    </p>*/}
                      {/*</div>*/}
                </li>
                
                {/*CO-Numbers*/}
                <li className={`${short ? 'hidden' : ''} col-span-1 flex justify-between items-center gap-x-2`}>
                      
                      <div className="flex flex-col gap-y-1">
                        <span className="block text-xs text-gray-500 text-muted-foreground-1">
                          CO-Numbers
                        </span>
                        <span className="block font-medium text-sm text-foreground">
                          <ERPBadgeList value={order.orderERP} />
                        </span>
                      </div>
                      
                    </li>
                
                {/*Score*/}
                <div className="col-span-1 relative flex items-center justify-between gap-1 flex-row-reverse">
                    
                    <PerformanceScoreRing score={score} size={48}/>
            
                    <div className={`${short ? 'hidden' : ''}`}>
                      <div className="flex flex-col">
                        <span className="block text-xs text-gray-500 text-muted-foreground-1">
                          Performance quality score
                        </span>
                        <div className="flex items-center gap-2">
                            <span className="block font-medium text-sm text-foreground">
                                {((score / 12) * 100).toFixed(0)} %
                            </span>
                            <span className="flex justify-center items-center gap-x-1 text-xs">
                                <Icon className={`size-3 ${meta.color}`} />
                                <span className={`text-xxs font-medium ${meta.color}`}>
                                    {meta.label}
                                </span>
                            </span>
                        </div>
                      </div>
                    </div>
                    
                </div>
          
            </div>
        
        </div>
        
    )
}

export default PerformanceCard;
